import { useState, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Upload,
  Download,
  FileSpreadsheet,
  Loader2,
  AlertCircle,
  CheckCircle,
  FileText,
} from 'lucide-react';
import { clsx } from 'clsx';
import toast from 'react-hot-toast';
import type { Store, Category, ImportResult } from '../types';

interface ImportExportProps {
  isOpen: boolean;
  onClose: () => void;
  stores: Store[];
  categories: Category[];
  onImport: (csvData: string, storeId?: string, categoryId?: string, updateExisting?: boolean) => Promise<ImportResult>;
  onExport: (storeId?: string, categoryId?: string, status?: string, format?: 'csv' | 'json') => Promise<any>;
  onDownloadTemplate: () => Promise<string>;
}

type Tab = 'import' | 'export';

export function ImportExport({
  isOpen,
  onClose,
  stores,
  categories,
  onImport,
  onExport,
  onDownloadTemplate,
}: ImportExportProps) {
  const [activeTab, setActiveTab] = useState<Tab>('import');
  const [isDragging, setIsDragging] = useState(false);
  const [csvContent, setCsvContent] = useState('');
  const [fileName, setFileName] = useState('');
  const [previewRows, setPreviewRows] = useState<string[][]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  
  // Import options
  const [importStoreId, setImportStoreId] = useState('');
  const [importCategoryId, setImportCategoryId] = useState('');
  const [updateExisting, setUpdateExisting] = useState(false);
  
  // Export options
  const [exportStoreId, setExportStoreId] = useState('');
  const [exportCategoryId, setExportCategoryId] = useState('');
  const [exportStatus, setExportStatus] = useState<'all' | 'active' | 'inactive'>('all');
  const [exportFormat, setExportFormat] = useState<'csv' | 'json'>('csv');
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  const parseCSVPreview = (content: string) => {
    const lines = content.split('\n').filter((line) => line.trim());
    const rows = lines.slice(0, 6).map((line) => {
      // Simple CSV parsing (doesn't handle all edge cases)
      const result: string[] = [];
      let current = '';
      let inQuotes = false;

      for (const char of line) {
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          result.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      result.push(current.trim());
      return result;
    });
    return rows;
  };

  const handleFile = useCallback((file: File) => {
    if (!file.name.endsWith('.csv')) {
      toast.error('Only CSV files are supported');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      setCsvContent(content);
      setFileName(file.name);
      setPreviewRows(parseCSVPreview(content));
      setImportResult(null);
    };
    reader.readAsText(file);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const file = e.dataTransfer.files[0];
      if (file) handleFile(file);
    },
    [handleFile]
  );

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    e.target.value = '';
  };

  const handleImport = async () => {
    if (!csvContent) {
      toast.error('Please select a CSV file first');
      return;
    }

    setIsProcessing(true);
    try {
      const result = await onImport(
        csvContent,
        importStoreId || undefined,
        importCategoryId || undefined,
        updateExisting
      );
      setImportResult(result);
      
      if (result.success) {
        toast.success(`Imported ${result.imported} products`);
      } else if (result.imported > 0) {
        toast.success(`Imported ${result.imported} products (${result.failed} failed)`);
      } else {
        toast.error('Import failed');
      }
    } catch (error) {
      toast.error('Import failed');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleExport = async () => {
    setIsProcessing(true);
    try {
      const data = await onExport(
        exportStoreId || undefined,
        exportCategoryId || undefined,
        exportStatus,
        exportFormat
      );

      // Download the file
      const blob = new Blob(
        [typeof data === 'string' ? data : JSON.stringify(data, null, 2)],
        { type: exportFormat === 'csv' ? 'text/csv' : 'application/json' }
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `products_${Date.now()}.${exportFormat}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success('Export downloaded');
    } catch (error) {
      toast.error('Export failed');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDownloadTemplate = async () => {
    setIsProcessing(true);
    try {
      const template = await onDownloadTemplate();
      const blob = new Blob([template], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'products_import_template.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch (error) {
      toast.error('Failed to download template');
    } finally {
      setIsProcessing(false);
    }
  };

  const resetImport = () => {
    setCsvContent('');
    setFileName('');
    setPreviewRows([]);
    setImportResult(null);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-slate-900 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-700">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <FileSpreadsheet className="text-orange-400" />
            Import / Export Products
          </h2>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-700">
          <button
            onClick={() => setActiveTab('import')}
            className={clsx(
              'flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b-2 -mb-px',
              activeTab === 'import'
                ? 'text-orange-400 border-orange-400'
                : 'text-slate-400 border-transparent hover:text-white'
            )}
          >
            <Upload size={16} />
            Import
          </button>
          <button
            onClick={() => setActiveTab('export')}
            className={clsx(
              'flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b-2 -mb-px',
              activeTab === 'export'
                ? 'text-orange-400 border-orange-400'
                : 'text-slate-400 border-transparent hover:text-white'
            )}
          >
            <Download size={16} />
            Export
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <AnimatePresence mode="wait">
            {activeTab === 'import' ? (
              <motion.div
                key="import"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="space-y-6"
              >
                {/* Import Result */}
                {importResult && (
                  <div
                    className={clsx(
                      'p-4 rounded-lg border',
                      importResult.success
                        ? 'bg-green-500/10 border-green-500/30'
                        : 'bg-red-500/10 border-red-500/30'
                    )}
                  >
                    <div className="flex items-start gap-3">
                      {importResult.success ? (
                        <CheckCircle className="text-green-400 shrink-0" size={16} />
                      ) : (
                        <AlertCircle className="text-red-400 shrink-0" size={16} />
                      )}
                      <div className="flex-1">
                        <p className="font-medium text-white">
                          {importResult.success ? 'Import Complete' : 'Import Completed with Errors'}
                        </p>
                        <p className="text-sm text-slate-400 mt-1">
                          {importResult.imported} imported, {importResult.failed} failed
                        </p>
                        {importResult.errors.length > 0 && (
                          <div className="mt-2 max-h-32 overflow-y-auto">
                            {importResult.errors.slice(0, 5).map((err, i) => (
                              <p key={i} className="text-xs text-red-400">
                                Row {err.row}: {err.error}
                              </p>
                            ))}
                            {importResult.errors.length > 5 && (
                              <p className="text-xs text-slate-500">
                                ...and {importResult.errors.length - 5} more errors
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Upload Area */}
                {!csvContent ? (
                  <div
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    className={clsx(
                      'border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-all',
                      isDragging
                        ? 'border-orange-500 bg-orange-500/10'
                        : 'border-slate-600 hover:border-slate-500'
                    )}
                  >
                    <Upload className="mx-auto mb-4 text-slate-500" size={48} />
                    <p className="text-lg text-white mb-2">
                      Drag & drop CSV file here
                    </p>
                    <p className="text-sm text-slate-400">
                      or{' '}
                      <span className="text-orange-400">click to browse</span>
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* File Info */}
                    <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <FileText className="text-green-400" size={24} />
                        <div>
                          <p className="font-medium text-white">{fileName}</p>
                          <p className="text-xs text-slate-400">
                            {previewRows.length - 1} rows found
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={resetImport}
                        className="text-slate-400 hover:text-white"
                      >
                        <X size={16} />
                      </button>
                    </div>

                    {/* Preview */}
                    {previewRows.length > 0 && (
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="border-b border-slate-700">
                              {previewRows[0].slice(0, 6).map((header, i) => (
                                <th
                                  key={i}
                                  className="text-left p-2 text-slate-400 font-medium"
                                >
                                  {header}
                                </th>
                              ))}
                              {previewRows[0].length > 6 && (
                                <th className="text-left p-2 text-slate-500">
                                  +{previewRows[0].length - 6} more
                                </th>
                              )}
                            </tr>
                          </thead>
                          <tbody>
                            {previewRows.slice(1).map((row, i) => (
                              <tr key={i} className="border-b border-slate-800">
                                {row.slice(0, 6).map((cell, j) => (
                                  <td key={j} className="p-2 text-slate-300 truncate max-w-[150px]">
                                    {cell || '-'}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  className="hidden"
                  onChange={handleFileChange}
                />

                {/* Import Options */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-1">
                      Import to Store
                    </label>
                    <select
                      value={importStoreId}
                      onChange={(e) => setImportStoreId(e.target.value)}
                      className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white"
                    >
                      <option value="">No store (global)</option>
                      {stores.map((store) => (
                        <option key={store.id} value={store.id}>
                          {store.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-1">
                      Default Category
                    </label>
                    <select
                      value={importCategoryId}
                      onChange={(e) => setImportCategoryId(e.target.value)}
                      className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white"
                    >
                      <option value="">No category</option>
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={updateExisting}
                    onChange={(e) => setUpdateExisting(e.target.checked)}
                    className="rounded border-slate-600"
                  />
                  <span className="text-sm text-slate-300">
                    Update existing products (match by SKU)
                  </span>
                </label>

                {/* Actions */}
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleImport}
                    disabled={!csvContent || isProcessing}
                    className="btn bg-orange-500 hover:bg-orange-600 text-white disabled:opacity-50"
                  >
                    {isProcessing ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        Importing...
                      </>
                    ) : (
                      <>
                        <Upload size={16} />
                        Import Products
                      </>
                    )}
                  </button>
                  <button
                    onClick={handleDownloadTemplate}
                    disabled={isProcessing}
                    className="btn bg-slate-700 hover:bg-slate-600 text-white"
                  >
                    <Download size={16} />
                    Download Template
                  </button>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="export"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="space-y-6"
              >
                <div className="text-center py-4">
                  <Download className="mx-auto mb-3 text-slate-500" size={48} />
                  <h3 className="text-lg font-semibold text-white mb-1">
                    Export Products
                  </h3>
                  <p className="text-sm text-slate-400">
                    Download your products as CSV or JSON
                  </p>
                </div>

                {/* Export Options */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-1">
                      Store
                    </label>
                    <select
                      value={exportStoreId}
                      onChange={(e) => setExportStoreId(e.target.value)}
                      className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white"
                    >
                      <option value="">All stores</option>
                      {stores.map((store) => (
                        <option key={store.id} value={store.id}>
                          {store.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-1">
                      Category
                    </label>
                    <select
                      value={exportCategoryId}
                      onChange={(e) => setExportCategoryId(e.target.value)}
                      className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white"
                    >
                      <option value="">All categories</option>
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-1">
                      Status
                    </label>
                    <select
                      value={exportStatus}
                      onChange={(e) => setExportStatus(e.target.value as any)}
                      className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white"
                    >
                      <option value="all">All products</option>
                      <option value="active">Active only</option>
                      <option value="inactive">Inactive only</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-1">
                      Format
                    </label>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setExportFormat('csv')}
                        className={clsx(
                          'flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                          exportFormat === 'csv'
                            ? 'bg-orange-500 text-white'
                            : 'bg-slate-800 text-slate-400 hover:text-white'
                        )}
                      >
                        CSV
                      </button>
                      <button
                        onClick={() => setExportFormat('json')}
                        className={clsx(
                          'flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                          exportFormat === 'json'
                            ? 'bg-orange-500 text-white'
                            : 'bg-slate-800 text-slate-400 hover:text-white'
                        )}
                      >
                        JSON
                      </button>
                    </div>
                  </div>
                </div>

                {/* Export Button */}
                <button
                  onClick={handleExport}
                  disabled={isProcessing}
                  className="w-full btn bg-orange-500 hover:bg-orange-600 text-white disabled:opacity-50"
                >
                  {isProcessing ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Exporting...
                    </>
                  ) : (
                    <>
                      <Download size={16} />
                      Export Products
                    </>
                  )}
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
}
